import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, Menu, X } from 'lucide-react';
import logoDark from '../assets/navartha-logo-dark.png';
import logoLight from '../assets/navartha-logo-light.png';
import { T } from '../i18n/copy';
import { clinicPath, homePath, switchPath } from '../i18n/paths';

const NAV_ITEMS = [
  { key: 'howWeWork', anchor: 'how-we-work', testid: 'nav-link-how-we-work' },
  { key: 'whereValue', anchor: 'where-we-create-value', testid: 'nav-link-where-we-create-value' },
  { key: 'about', anchor: 'about-navartha', testid: 'nav-link-about-navartha' },
  { key: 'clinic', anchor: 'business-clinic', testid: 'nav-link-business-clinic' },
];

const THEMES = {
  bone: {
    bar: 'bg-[#F2F0E8]/90 border-midnight/10 text-midnight',
    logo: logoDark,
    link: 'text-midnight/65 hover:text-midnight',
    cta: 'bg-vermilion text-bone hover:bg-[#A8402C]',
    sub: 'text-midnight/40',
  },
  midnight: {
    bar: 'bg-[#141C26]/90 border-bone/10 text-bone',
    logo: logoLight,
    link: 'text-bone/65 hover:text-bone',
    cta: 'bg-vermilion text-bone hover:bg-[#A8402C]',
    sub: 'text-bone/40',
  },
  vermilion: {
    bar: 'bg-[#C44B35]/95 border-bone/20 text-bone',
    logo: logoLight,
    link: 'text-bone/80 hover:text-bone',
    cta: 'bg-midnight text-bone hover:bg-[#0E141B]',
    sub: 'text-bone/60',
  },
};

const LangSwitch = ({ lang, theme, onSwitch }) => (
  <div className="flex items-center gap-1.5" data-testid="nav-language-switcher">
    <button
      type="button"
      data-testid="lang-switch-en"
      onClick={() => lang !== 'en' && onSwitch('en')}
      className={`text-[12px] font-medium tracking-[0.14em] transition-opacity duration-200 ${lang === 'en' ? 'opacity-100' : 'opacity-55 hover:opacity-100'}`}
      aria-pressed={lang === 'en'}
      aria-label="Switch to English"
    >
      EN
    </button>
    <span className="h-3 w-px bg-current opacity-30" aria-hidden="true" />
    <button
      type="button"
      data-testid="lang-switch-id"
      onClick={() => lang !== 'id' && onSwitch('id')}
      className={`text-[12px] font-medium tracking-[0.14em] transition-opacity duration-200 ${lang === 'id' ? 'opacity-100' : 'opacity-55 hover:opacity-100'}`}
      aria-pressed={lang === 'id'}
      aria-label="Beralih ke Bahasa Indonesia"
    >
      ID
    </button>
  </div>
);

export default function Header({ lang }) {
  const [theme, setTheme] = useState('bone');
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const raf = useRef(null);
  const t = THEMES[theme];
  const home = homePath(lang);
  const clinic = clinicPath(lang);

  useEffect(() => {
    const measure = () => {
      raf.current = null;
      const els = document.querySelectorAll('[data-header-theme]');
      let next = 'bone';
      els.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.top <= 40 && r.bottom > 40) next = el.dataset.headerTheme;
      });
      setTheme((prev) => (prev === next ? prev : next));
    };
    const onScroll = () => {
      if (raf.current == null) raf.current = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, [location.pathname]);

  useEffect(() => setOpen(false), [location.pathname, location.hash]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    if (open) window.__lenis?.stop();
    else window.__lenis?.start();
    return () => {
      document.body.style.overflow = '';
      window.__lenis?.start();
    };
  }, [open]);

  const anchorClick = (e, anchor) => {
    const onHome = location.pathname === home;
    if (onHome) {
      e.preventDefault();
      const el = document.getElementById(anchor);
      if (!el) return;
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: -76, duration: 1.2 });
      else el.scrollIntoView({ behavior: 'smooth' });
      window.history.replaceState(null, '', `#${anchor}`);
    }
  };

  const switchLang = (target) => {
    navigate(`${switchPath(location.pathname, target)}${location.hash}`);
  };

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 border-b backdrop-blur-md transition-colors duration-300 ${t.bar}`}
        style={{ height: '68px' }}
      >
        <div className="editorial-margins flex h-full items-center justify-between gap-6">
          <Link to={home} data-testid="header-logo-link" aria-label="Navartha — home" className="shrink-0">
            <img src={t.logo} alt="Navartha — AI Transformation & Implementation Firm" className="h-auto w-[120px] md:w-[150px]" />
          </Link>

          <nav className="hidden items-center gap-5 whitespace-nowrap min-[1360px]:flex" aria-label="Primary">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.key}
                to={item.key === 'clinic' ? `${home}#business-clinic` : `${home}#${item.anchor}`}
                data-testid={item.testid}
                onClick={(e) => anchorClick(e, item.key === 'clinic' ? 'business-clinic' : item.anchor)}
                className={`eyebrow transition-opacity duration-200 ${t.link}`}
              >
                {T.nav[item.key][lang]}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-4 md:gap-5">
            <div className="shrink-0">
              <LangSwitch lang={lang} theme={theme} onSwitch={switchLang} />
            </div>
            <Link
              to={clinic}
              data-testid="header-cta-button-mobile"
              className="group inline-flex items-center gap-2 whitespace-nowrap bg-vermilion px-3.5 py-2 text-[11px] font-medium tracking-[0.05em] text-bone transition-colors duration-300 hover:bg-[#A8402C] md:hidden"
            >
              {T.nav.clinic[lang]}
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5" strokeWidth={2} />
            </Link>
            <Link
              to={clinic}
              data-testid="header-cta-button"
              className="group hidden items-center gap-2 whitespace-nowrap bg-vermilion px-5 py-2.5 text-[12px] font-medium tracking-[0.08em] text-bone transition-colors duration-300 hover:bg-[#A8402C] md:inline-flex"
            >
              {T.nav.cta[lang]}
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5" strokeWidth={2} />
            </Link>
            <button
              type="button"
              data-testid="mobile-menu-button"
              aria-label={T.nav.menu[lang]}
              aria-expanded={open}
              onClick={() => setOpen(true)}
              className="inline-flex p-1.5 min-[1360px]:hidden"
            >
              <Menu className="h-5 w-5" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>
      <div aria-hidden="true" className="h-[68px]" />

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[70] flex flex-col bg-midnight text-bone"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="editorial-margins flex h-[68px] items-center justify-between border-b border-bone/10">
              <img src={logoLight} alt="Navartha" className="h-auto w-[120px]" />
              <button
                type="button"
                data-testid="mobile-menu-close"
                aria-label={T.nav.close[lang]}
                onClick={() => setOpen(false)}
                className="p-1.5"
              >
                <X className="h-6 w-6" strokeWidth={1.25} />
              </button>
            </div>
            <nav className="editorial-margins flex flex-1 flex-col justify-center gap-7" aria-label="Mobile">
              {NAV_ITEMS.map((item, i) => (
                <Link
                  key={item.key}
                  to={item.key === 'clinic' ? clinic : `${home}#${item.anchor}`}
                  data-testid={`mobile-nav-link-${item.anchor}`}
                  className="flex items-baseline gap-4"
                >
                  <span className="eyebrow text-vermilion">0{i + 1}</span>
                  <span className="font-serif text-3xl">{T.nav[item.key][lang]}</span>
                </Link>
              ))}
            </nav>
            <div className="editorial-margins flex items-center justify-between border-t border-bone/10 py-6">
              <LangSwitch lang={lang} theme="midnight" onSwitch={switchLang} />
              <Link
                to={clinic}
                data-testid="mobile-cta-button"
                className="inline-flex items-center gap-2 bg-vermilion px-5 py-3 text-[12px] font-medium tracking-[0.08em] text-bone"
              >
                {T.nav.cta[lang]}
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
